import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  CreditCard,
  RefreshCw,
  XCircle,
} from 'lucide-react'
import styles from './ActionRequired.module.css'
import { fmtEUR, fmtInt } from '../../../utils/moneyFormat'
import { formatDMYTime } from '../../../utils/dateFormat'
import { severityLabel } from '../../../screens/System&Settings/Monitoring/incidentsView'
import { actionRequiredView, MONITORING_PATH } from './actionRequiredView'

// "Action required" (UI-28): the compact list at the top of the dashboard of what someone must
// act on now. Paid online bookings that are not confirmed come first, in red, then every
// unresolved incident, critical first. The green "Everything is running" line appears only when
// actionRequiredView says it may: never on a failed request or an old or missing check.
//
// The header bell lists the same urgent bookings as notifications; this section is the
// dashboard's own read-out and keeps no read/unread state.

const linkProps = (navigate, href) =>
  href
    ? {
        role: 'link',
        tabIndex: 0,
        onClick: () => navigate(href),
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault()
            navigate(href)
          }
        },
      }
    : {}

const BookingRow = ({ item, navigate }) => {
  const b = item.booking
  const types = Array.isArray(b.productTypes) ? b.productTypes.filter(Boolean).join(', ') : ''
  return (
    <div
      className={`${styles.row} ${styles.rowUrgent} ${item.href ? styles.rowLink : ''}`}
      {...linkProps(navigate, item.href)}
    >
      <span className={`${styles.chip} ${styles.chipUrgent}`}>
        <CreditCard size={15} strokeWidth={2} />
      </span>
      <div className={styles.text}>
        <span className={styles.urgentTitle}>URGENT: Payment received, booking not confirmed</span>
        <span className={styles.meta}>
          <span className={styles.ref}>{b.bookingReference || 'No reference'}</span>
          {b.customer && <span className={styles.metaItem}>{b.customer}</span>}
          {b.amount !== null && b.amount !== undefined && (
            <span className={`${styles.metaItem} ${styles.amount} dshNum`}>{fmtEUR(b.amount)}</span>
          )}
          {types && <span className={styles.metaItem}>{types}</span>}
        </span>
      </div>
      {item.href && <ChevronRight size={15} strokeWidth={2} className={styles.chevron} />}
    </div>
  )
}

const IncidentRow = ({ item, navigate }) => {
  const i = item.incident
  const critical = i.severity === 'critical'
  return (
    <div className={`${styles.row} ${styles.rowLink}`} {...linkProps(navigate, item.href)}>
      <span className={`${styles.chip} ${critical ? styles.chipUrgent : styles.chipWarn}`}>
        <AlertTriangle size={15} strokeWidth={2} />
      </span>
      <div className={styles.text}>
        <span className={styles.title}>
          <span className={styles.titleText}>{i.title}</span>
          <span className={`${styles.tag} ${critical ? styles.tagCritical : styles.tagWarning}`}>
            {severityLabel(i.severity)}
          </span>
          <span className={styles.tag}>{i.status}</span>
        </span>
        <span className={styles.meta}>
          {i.cause && <span className={styles.cause}>{i.cause}</span>}
          <span className={styles.metaItem}>since {formatDMYTime(i.firstSeenAt)}</span>
        </span>
      </div>
      <ChevronRight size={15} strokeWidth={2} className={styles.chevron} />
    </div>
  )
}

const ActionRequired = ({ data, error, onRetry, now }) => {
  const navigate = useNavigate()
  const view = actionRequiredView({ data, error, now: now ?? Date.now() })

  if (view.mode === 'loading') {
    return (
      <section className={`dshPanel ${styles.root}`} aria-label="Action required" aria-busy="true">
        <div className={styles.line}>
          <span className={`dshSkel ${styles.skelIcon}`} />
          <span className={`dshSkel ${styles.skelLine}`} />
        </div>
      </section>
    )
  }

  if (view.mode === 'allClear') {
    return (
      <section className={`dshPanel ${styles.root} ${styles.clear}`} aria-label="Action required">
        <div className={styles.line}>
          <CheckCircle2 size={16} strokeWidth={2.2} className={styles.lineIcon} />
          <span className={styles.lineText}>Everything is running</span>
          <span className={styles.lineMeta}>Last check {formatDMYTime(view.lastCheckAt)}</span>
        </div>
      </section>
    )
  }

  if (view.mode === 'error') {
    return (
      <section className={`dshPanel ${styles.root} ${styles.failed}`} aria-label="Action required">
        <div className={styles.line}>
          <XCircle size={16} strokeWidth={2.2} className={styles.lineIcon} />
          <span className={styles.lineText}>
            Could not check what needs action. <span className={styles.lineDetail}>{view.error}</span>
          </span>
          {onRetry && (
            <button type="button" className={`dshAction ${styles.retry}`} onClick={onRetry}>
              <RefreshCw size={12} strokeWidth={2.4} /> Try again
            </button>
          )}
        </div>
      </section>
    )
  }

  if (view.mode === 'unverified') {
    return (
      <section className={`dshPanel ${styles.root} ${styles.unverified}`} aria-label="Action required">
        <div className={`${styles.line} ${styles.lineLink}`} {...linkProps(navigate, MONITORING_PATH)}>
          <Clock size={16} strokeWidth={2.2} className={styles.lineIcon} />
          <span className={styles.lineText}>{view.reason}</span>
          <span className={styles.lineAction}>
            Open Monitoring <ChevronRight size={14} strokeWidth={2.2} />
          </span>
        </div>
      </section>
    )
  }

  const urgent = view.items.some((it) => it.kind === 'booking' || it.incident?.severity === 'critical')

  return (
    <section className={`dshPanel ${styles.root} ${urgent ? styles.urgent : ''}`} aria-label="Action required">
      <div className={styles.head}>
        <div className={styles.headLeft}>
          <AlertTriangle size={15} strokeWidth={2.2} className={urgent ? styles.headIconUrgent : styles.headIcon} />
          <h3 className="dshTitle">Action required</h3>
        </div>
        <span className={`dshPill dshNum ${urgent ? styles.pillUrgent : 'dshPillWarn'}`}>
          {fmtInt(view.items.length)} to act on
        </span>
      </div>

      {view.error && (
        <div className={styles.staleNote}>
          <XCircle size={13} strokeWidth={2.2} />
          <span>Could not refresh this list ({view.error}). It shows what was last loaded.</span>
          {onRetry && (
            <button type="button" className={`dshAction ${styles.retry}`} onClick={onRetry}>
              <RefreshCw size={12} strokeWidth={2.4} /> Try again
            </button>
          )}
        </div>
      )}

      <div className={styles.list}>
        {view.items.map((item) =>
          item.kind === 'booking' ? (
            <BookingRow key={item.key} item={item} navigate={navigate} />
          ) : (
            <IncidentRow key={item.key} item={item} navigate={navigate} />
          )
        )}
      </div>
    </section>
  )
}

export default ActionRequired
